"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Article } from "@/app/types/blogTypes";
import BlogCard from "./blog-card";

type MoreArticlesProps = {
  articles: Article[];
};

export function MoreArticles({ articles }: MoreArticlesProps) {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);
    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  const slidesToShow = isDesktop ? 2.5 : 1;

  return (
    <CarouselLayout
      articles={articles}
      slidesToShow={slidesToShow}
      isDesktop={isDesktop}
    />
  );
}

type CarouselLayoutProps = {
  articles: Article[];
  slidesToShow: number;
  isDesktop: boolean;
};

function CarouselLayout({
  articles,
  slidesToShow,
  isDesktop,
}: CarouselLayoutProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalItems = articles.length;

  const maxIndex = Math.max(0, Math.ceil(totalItems - slidesToShow));

  const canGoNext = currentIndex < maxIndex;
  const canGoPrev = currentIndex > 0;

  const next = () => {
    if (canGoNext) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prev = () => {
    if (canGoPrev) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const trackWidthPercent = (totalItems * 100) / slidesToShow;
  const itemWidth = 100 / totalItems;
  const offsetPercent = currentIndex * itemWidth;

  const gapClasses = isDesktop ? "gap-6" : "gap-0";

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.2 } },
  };

  return (
    <div className="w-full pb-6 pt-40">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Ver mais artigos</h2>
        <div className="flex gap-2">
          <motion.button
            onClick={prev}
            disabled={!canGoPrev}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="h-10 w-10 flex items-center justify-center rounded-full border transition-colors duration-200 hover:bg-gray-50"
          >
            <ChevronLeft
              className={canGoPrev ? "text-green-800" : "text-gray-300"}
            />
          </motion.button>
          <motion.button
            onClick={next}
            disabled={!canGoNext}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="h-10 w-10 flex items-center justify-center rounded-full border transition-colors duration-200 hover:bg-gray-50"
          >
            <ChevronRight
              className={canGoNext ? "text-green-800" : "text-gray-300"}
            />
          </motion.button>
        </div>
      </div>

      <div className="relative overflow-hidden w-full">
        <motion.div
          className={`flex ${gapClasses}`}
          style={{
            width: `${trackWidthPercent}%`,
          }}
          initial={false}
          animate={{
            x: `-${offsetPercent}%`,
          }}
          transition={{
            duration: 0.5,
            ease: [0.32, 0.72, 0, 1],
            type: "spring",
            stiffness: 150,
            damping: 30,
          }}
        >
          {articles.map((article) => (
            <div
              key={article.id}
              className="shrink-0"
              style={{
                width: `${itemWidth}%`,
                paddingRight: isDesktop ? "16px" : "8px",
              }}
            >
              <BlogCard article={article} />
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
